import React from 'react';
import { GeofenceCategory } from '@models/geofence.model';
import {
  GeofenceCategoryIcon,
  DEFAULT_GEOFENCE_COLOR,
} from '@utils/geofenceCategoryIcons';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';

interface GeofenceCategoriesGridProps {
  categories: GeofenceCategory[];
  onEdit?: (category: GeofenceCategory) => void;
  onDelete?: (category: GeofenceCategory) => void;
  /** When provided, renders an "Add Category" tile as the first item in the grid. */
  onAdd?: () => void;
}

const Card: React.FC<{
  category: GeofenceCategory;
  onEdit?: (c: GeofenceCategory) => void;
  onDelete?: (c: GeofenceCategory) => void;
}> = ({ category, onEdit, onDelete }) => {
  const color = category.color || DEFAULT_GEOFENCE_COLOR;
  const showActions = !category.isGlobal && (onEdit || onDelete);

  return (
    <div
      className={`bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 relative group transition-all hover:shadow-md ${
        !category.isActive ? 'opacity-60' : ''
      }`}
    >
      {/* Icon */}
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
        style={{ backgroundColor: `${color}20`, color }}
      >
        <GeofenceCategoryIcon iconKey={category.icon} sx={{ fontSize: 22 }} />
      </div>

      {/* Name & Description */}
      <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
      {category.description && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{category.description}</p>
      )}

      {/* Status & Global */}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            category.isActive
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {category.isActive ? 'Active' : 'Inactive'}
        </span>
        {category.isGlobal && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            <PublicIcon sx={{ fontSize: 12 }} />
            Global
          </span>
        )}
        <span className="text-xs text-gray-400">Sort {category.sortOrder ?? 0}</span>
      </div>

      {/* Hover Actions (hidden for global categories) */}
      {showActions && (
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(category)}
              className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-indigo-600 hover:ring-indigo-200"
              title="Edit"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(category)}
              className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-red-600 hover:ring-red-200"
              title="Delete"
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const AddTile: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 transition-colors min-h-[140px]"
  >
    <AddIcon sx={{ fontSize: 24 }} />
    <span className="text-sm font-medium">Add Category</span>
  </button>
);

export const GeofenceCategoriesGrid: React.FC<GeofenceCategoriesGridProps> = ({
  categories,
  onEdit,
  onDelete,
  onAdd,
}) => {
  if (categories.length === 0 && !onAdd) {
    return (
      <div className="text-center py-12 text-sm text-gray-500">
        No geofence categories yet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {onAdd && <AddTile onClick={onAdd} />}
      {categories.map((category) => (
        <Card
          key={category.geofenceCategoryId}
          category={category}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default GeofenceCategoriesGrid;
